import type { Step } from 'react-joyride';
import type { NavigateFunction } from 'react-router-dom';

export const TUTORIAL_FLAG = 'tutorial_completed_v2';

interface RawStep {
  path: string;
  target: string;
  title: string;
  content: string;
  placement: 'top' | 'bottom' | 'left' | 'right';
}

const RAW: RawStep[] = [
  // ---- Home: wallet card area
  { path: '/home', target: '[data-tour="wallet-card"]',
    placement: 'bottom',
    title: 'Your wallet',
    content: 'Your one balance for every game. Top it up once, then load any game in seconds. Tap the eye icon to hide your balance.' },
  { path: '/home', target: '[data-tour="wallet-card-withdraw"]',
    placement: 'left',
    title: 'Withdraw the wallet',
    content: 'This Withdraw button cashes out your wallet balance straight to a bank or payment method.' },
  { path: '/home', target: '[data-tour="wallet-card-topup"]',
    placement: 'left',
    title: 'Top up the wallet',
    content: 'This Top-Up button on the wallet card adds money to your wallet using your preferred payment method.' },

  // ---- Home: 6 quick actions
  { path: '/home', target: '[data-tour="qa-wallet-topup"]',
    placement: 'bottom',
    title: 'Wallet Top-Up',
    content: 'Same as the button on the wallet card. Adds money to your wallet.' },
  { path: '/home', target: '[data-tour="qa-game-topup"]',
    placement: 'bottom',
    title: 'Game Top-Up',
    content: 'Load credits from your wallet (or a payment method) directly into a game.' },
  { path: '/home', target: '[data-tour="qa-withdraw"]',
    placement: 'top',
    title: 'Withdraw',
    content: 'Cash out winnings from a game, or your wallet balance, to a bank.' },
  { path: '/home', target: '[data-tour="qa-support"]',
    placement: 'top',
    title: 'Support',
    content: 'Opens our support chat if you need help with anything.' },
  { path: '/home', target: '[data-tour="qa-promo"]',
    placement: 'top',
    title: 'Promo Code',
    content: 'Got a code? Tap to redeem it; the credit lands straight in your wallet.' },
  { path: '/home', target: '[data-tour="qa-referral"]',
    placement: 'top',
    title: 'Refer & Earn',
    content: 'Share your unique code. When a friend signs up and tops up, you both get rewarded.' },

  // ---- Bottom nav (single overview)
  { path: '/home', target: '[data-tour="bottom-nav"]',
    placement: 'top',
    title: 'Bottom navigation',
    content: 'Home (you are here), Games (browse + request access), Top-Up, Withdraw, and Profile (history & settings).' },

  // ---- Cross-page walkthrough
  { path: '/games', target: '[data-tour="games-header"]',
    placement: 'bottom',
    title: 'Games tab',
    content: 'Browse every game, filter by Hot, or search by name.' },
  { path: '/games', target: '[data-tour="game-card-first"]',
    placement: 'bottom',
    title: 'Tap a game',
    content: 'Tap any card to request credentials. Once approved, tap the same card to view them, or hit the download icon if a download is available.' },
  { path: '/topup', target: '[data-tour="topup-form"]',
    placement: 'bottom',
    title: 'Top-Up flow',
    content: 'Pick a game, choose a payment method (or pay from your wallet), enter the amount, submit.' },
  { path: '/withdraw', target: '[data-tour="withdraw-choice"]',
    placement: 'top',
    title: 'Withdraw',
    content: 'Three paths: redeem game winnings into your wallet, cash out from a game directly to a bank, or send your wallet balance to a bank.' },
  { path: '/profile', target: '[data-tour="profile-history"]',
    placement: 'bottom',
    title: 'Order history',
    content: 'Every top-up and withdrawal with live status. Notification preferences and avatar live in this section too.' },
  { path: '/profile', target: '[data-tour="profile-howitworks"]',
    placement: 'bottom',
    title: 'How it works',
    content: 'Opens a written guide to every feature. Use it whenever you need a refresher.' },
  { path: '/profile', target: '[data-tour="profile-replay"]',
    placement: 'top',
    title: "You're all set",
    content: "Tap 'Replay Tutorial' anytime to walk through this again." },
];

const baseStep = {
  buttons: ['back', 'primary', 'skip'] as ('back' | 'primary' | 'skip' | 'close')[],
  skipBeacon: true,
};

export function buildSteps(navigate: NavigateFunction, getPath: () => string): Step[] {
  const wait = (ms: number) => new Promise<void>(r => setTimeout(r, ms));
  return RAW.map(({ path, target, title, content, placement }) => ({
    ...baseStep,
    target,
    title,
    content,
    placement,
    before: async () => {
      if (getPath() !== path) {
        navigate(path);
        // Allow the route to mount and the target element to render.
        await wait(500);
      }
    },
  }));
}

export const TUTORIAL_STEP_COUNT = RAW.length;
